/**
 * NoteTemplateMenu — icon-button menu (floating listbox) that inserts a saved
 * note template into a bound textarea (quote-level Notes, line-item note, etc.).
 *
 * Named `Menu` (not `Picker`) so the conformance linter recognizes this as a
 * floating overlay surface (OVERLAY regex allows shadow-md on filenames
 * containing Modal|Dialog|Popover|Dropdown|Menu|...). Behavior identical to
 * what PR-64 §1.2 describes; only the filename changed to keep conformance
 * without a per-file lint suppression.
 *
 * PR-64 §1.2 (R-25 Adrian, G-20 Sarah). Two consumers as of this commit:
 *   • components/quotes/builder/QuoteBuilderPage.js         → quote-level notes (metadata.notes)
 *   • components/quotes/builder/AddItemGroups.js            → line-item note in the wizard's ExtrasAccordion (IDI-176: same consumer, different mount point since EditItemModal was retired 2026-09-05)
 *
 * NoteModal.js is a Family B modal in BOTH QuoteBuilderPage and
 * ChangeOrderBuilderPage — imported/rendered but no user-facing opener wires
 * `setNoteModalItem` to a truthy value. Cleanup out of PR-64 scope.
 *
 * VOCAB (verified at NoteTemplatesSection.js:14):
 *   template shape = { id, name, note, active, tags }
 *   canonical body field is `note` (SINGULAR). Reject body / content / text on sight.
 *
 * Insertion behavior: append with a blank line between existing content and the
 * template body (non-destructive; requires no cursor ref).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { settingsAPI } from '../../../services/api';

/**
 * Compose new textarea value = existing + '\n\n' + templateBody.
 * Empty existing → just the body.
 * Exported for direct unit test.
 */
export function appendTemplate(currentValue, templateBody) {
  const existing = (currentValue == null ? '' : String(currentValue));
  const body = (templateBody == null ? '' : String(templateBody));
  if (!existing) return body;
  return existing + '\n\n' + body;
}

export default function NoteTemplateMenu({
  currentValue = '',
  onSelect,
  buttonSize = 'sm',
  label = 'Insert from template',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const containerRef = useRef(null);

  // Fetch templates on first open — cheap enough to re-fetch each open so
  // Settings edits show up without a page reload.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await settingsAPI.getList('note_templates');
      // getList already normalizes to an array; defensively re-check anyway.
      const arr = Array.isArray(res) ? res : [];
      setTemplates(arr);
    } catch (e) {
      setError('Could not load templates.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const activeTemplates = templates.filter((t) => t && t.active !== false);

  const iconSizeClass = buttonSize === 'md' ? 'h-5 w-5' : 'h-4 w-4';
  const buttonPadClass = buttonSize === 'md' ? 'p-1.5' : 'p-1';

  const handlePick = (tpl) => {
    if (!tpl || typeof onSelect !== 'function') return;
    // Canonical body field is `note` (singular). Do NOT read tpl.body / tpl.content.
    const body = tpl.note == null ? '' : String(tpl.note);
    onSelect(appendTemplate(currentValue, body));
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${buttonPadClass} rounded hover:bg-[color:var(--neutral-100)]`}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label}
      >
        <DocumentTextIcon
          className={iconSizeClass}
          style={{ color: 'var(--color-text-muted)' }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Note templates"
          className="absolute right-0 z-20 mt-1 w-64 rounded-md border shadow-md"
          style={{
            backgroundColor: 'var(--color-surface-default)',
            borderColor: 'var(--color-border)',
          }}
        >
          {loading && (
            <div
              className="p-3 text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Loading…
            </div>
          )}

          {!loading && error && (
            <div
              className="p-3 text-sm"
              style={{ color: 'var(--color-danger)' }}
              role="alert"
            >
              {error}
            </div>
          )}

          {!loading && !error && activeTemplates.length === 0 && (
            <div
              className="p-3 text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              No templates. Add some in Settings → Quote Templates.
            </div>
          )}

          {!loading && !error && activeTemplates.length > 0 && (
            <ul className="py-1 max-h-64 overflow-auto">
              {activeTemplates.map((tpl) => (
                <li key={tpl.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => handlePick(tpl)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--neutral-100)]"
                    style={{ color: 'var(--color-text-primary)' }}
                    title={tpl.note || ''}
                  >
                    {tpl.name || '(untitled)'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
